import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ProductIntroGallery from '../components/ProductIntroGallery';
import { RootState, AppDispatch } from '../store';
import {
	Settings,
	Platform,
	DeviceType,
	Orientation,
	WriteDirection,
	GalleryItem,
	PredefinedConfiguration,
	RangeSettings,
	ShoppingGalleryItem,
	Lightbox,
	CoverItem,
	ProductCardItem,
} from 'sofa-reborn-react';
import {
	setCurrentLightbox,
	updateIframeHeight,
	resetIframeHeight,
	setLoadingCovers,
	setProductVariantsShowMore,
	setProductCardTypeClick,
} from '../store/application/actions';
import {
	setGalleryPredefinedConfigurationId,
	setPersistedConfiguration,
	setGalleryVpcCode,
	setCloseGallery,
} from '../store/sofaConfiguration/actions';
import { resetScreensaverTimeout } from '../store/screensaver/actions';
import { UrlService } from '../services';
import { hidePopOver } from '../store/popOver/actions';
import { setShowOpenDesign } from '../store/openDesign/actions';

import { setKeepSceneHeight } from '../store/scene/actions';

import { localeService } from '../services';
import { clearAdditionalCoverItems, setCoverOnly, setSelectedCover } from '../store/cover';
import { setCoverItem } from '../store/application/thunks';
import { ApplicationUtils } from '../util/ApplicationUtils';
import { IDexfSettings, ITranslations } from '@inter-ikea-kompis/types';
import ProductCardModalEnum from '@inter-ikea-kompis/component-product-card/lib/enums/ProductCardModalEnum';

const ProductIntroGalleryContainer: React.FC = () => {
	const [element, setElement] = useState<HTMLElement>();
	const [resizeObserver, setResizeObserver] = useState<ResizeObserver>();
	const [visibleModal, setVisibleModal] = useState<ProductCardModalEnum | null>(null);
	const [ecoFeeSheetVisibleId, setEcoFeeSheetVisibleId] = useState<string | null>(null);

	const galleryItems = useSelector<RootState, GalleryItem[]>(
		(state) => state.products.galleryItems
	);
	const galleryShoppingProducts = useSelector<RootState, ShoppingGalleryItem[] | []>(
		(state) => state.products.galleryShoppingProducts
	);
	const platform = useSelector<RootState, Platform>((state) => state.deviceInfo.platform);
	const deviceType = useSelector<RootState, DeviceType>((state) => state.deviceInfo.deviceType);
	const orientation = useSelector<RootState, Orientation>((state) => state.deviceInfo.orientation);
	const writeDirection = useSelector<RootState, WriteDirection>(
		(state) => state.localization.writeDirection
	);
	const settings = useSelector<RootState, Settings>((state) => state.settings.settings);
	const rangeSettings = useSelector<RootState, RangeSettings>(
		(state) => state.settings.rangeSettings as RangeSettings
	);

	const kompisSettings = useSelector<RootState, IDexfSettings | null>(
		(state) => state.settings.kompisSettings
	);
	const kompisTranslations = useSelector<RootState, ITranslations | null>(
		(state) => state.localization.kompisTranslations
	);
	const currentLightbox = useSelector<RootState, Lightbox>(
		(state) => state.application.currentLightbox
	);
	const showOpenDesign = useSelector<RootState, boolean>(
		(state) => state.openDesign.showOpenDesign
	);

	const predefinedConfigurations = useSelector<RootState, PredefinedConfiguration[]>(
		(state) => state.settings.predefinedConfigurations
	);
	const covers = useSelector<RootState, CoverItem[]>((state) => state.cover.coverItems);
	const showScreensaver = useSelector<RootState, boolean>(
		(state) => state.screensaver.showScreensaver
	);
	const hasVisitScene = useSelector<RootState, boolean>((state) => state.application.hasVisitScene);
	const notPreloadedCovers = useSelector<RootState, string[] | null>(
		(state) => state.cover.notPreloadedCovers
	);
	const coverOnly = useSelector<RootState, boolean | undefined>((state) => state.cover.coverOnly);
	const coverOnlyItems = useSelector<RootState, CoverItem[]>((state) => state.cover.coverOnlyItems);
	const selectedCover = useSelector<RootState, CoverItem>(
		(state) => state.cover.selectedCover as CoverItem
	);
	const isInUpptackaPlatform = useSelector<RootState, boolean>(
		(state) => state.application.isInUpptackaPlatform
	);
	const isProduction = ApplicationUtils.isProduction();

	const dispatch = useDispatch<AppDispatch>();

	useEffect(() => {
		if (showScreensaver) {
			setVisibleModal(null);
		}
	}, [showScreensaver]);

	useEffect(() => {
		if (currentLightbox === 'product-intro-gallery') {
			dispatch(setKeepSceneHeight(true));
			const onResize = (element: ResizeObserverEntry[]): void => {
				if (element) {
					if (visibleModal || showOpenDesign) {
						dispatch(resetIframeHeight());
					} else {
						dispatch(updateIframeHeight((element[0].target as HTMLElement).offsetHeight));
					}
				}
			};
			const resizeObserver = new ResizeObserver(onResize);
			setResizeObserver(resizeObserver);
			const element = document.getElementById('productIntroGallery');
			if (element) {
				setElement(element);
				resizeObserver.observe(element);
			} else {
				throw 'ProductIntroGalleryContainer: useEffect(): element does not exist';
			}

			return (): void => {
				dispatch(resetScreensaverTimeout());
				if (element) {
					resizeObserver.unobserve(element);
					dispatch(resetIframeHeight());
				}
				setTimeout(() => {
					dispatch(setKeepSceneHeight(false));
				}, 100);
			};
		}
		// eslint-disable-next-line @typescript-eslint/no-empty-function
		return (): void => {};
	}, [currentLightbox, visibleModal, showOpenDesign]);

	const resetSwiper = (): void => {
		if (platform === 'kiosk') {
			const productViewWrapperElement = document.getElementById('product-view');

			if (productViewWrapperElement) {
				productViewWrapperElement.scrollTop = 0;
			}
		}
	};

	const getNrPerRow = (): number => {
		return deviceType === 'mobile'
			? orientation === 'portrait'
				? 1
				: 2
			: deviceType === 'tablet'
			? 3
			: 4;
	};

	const getNrOfEmptyElements = (): number => {
		const nrPerRow: number = getNrPerRow();
		/* Adding 2 because of the start from scratch item and cover only item */
		const itemsLength = galleryItems.length + 2;

		return itemsLength % nrPerRow !== 0 ? nrPerRow - (itemsLength % nrPerRow) : 0;
	};

	const getStartFromScratchElementIndex = (): number => {
		const startFromScratchIndex = deviceType === 'mobile' ? 1 : deviceType === 'tablet' ? 2 : 0;
		return galleryItems.length >= startFromScratchIndex + 1
			? startFromScratchIndex
			: galleryItems.length;
	};

	const onSelectItem = (item: ProductCardItem, showCoverPicker: boolean): void => {
		if (element && resizeObserver) {
			resizeObserver.unobserve(element);
			dispatch(resetIframeHeight());
		}

		const predefinedConfiguration: PredefinedConfiguration | undefined =
			predefinedConfigurations.find((configuration) => {
				return configuration.id === item.id;
			});

		if (predefinedConfiguration && item.id) {
			if (
				predefinedConfiguration.defaultColor &&
				notPreloadedCovers &&
				notPreloadedCovers.includes(predefinedConfiguration.defaultColor)
			) {
				dispatch(setLoadingCovers(true));
			}
			dispatch(setPersistedConfiguration(null));
			const coverItem = covers.find(
				(cover) => cover.coverName === predefinedConfiguration.defaultColor
			);
			if (coverItem) {
				dispatch(setCoverItem(coverItem, false));
			}
			dispatch(setGalleryPredefinedConfigurationId(item.id));
			/* Add # with predefined configuration to url */
			const newHash = UrlService.getReplacedIdHash(item.id);
			UrlService.setBrowserWindowHash(newHash);
		}

		dispatch(setProductCardTypeClick(item));

		if (coverOnly) {
			dispatch(setCoverOnly(false));
		}

		if (showCoverPicker) {
			dispatch(setCurrentLightbox('cover-picker'));
			dispatch(setProductVariantsShowMore(true));
		} else {
			dispatch(setCurrentLightbox('none'));
		}
		resetSwiper();

		dispatch(
			hidePopOver({
				type: 'all',
			})
		);
		dispatch(setShowOpenDesign(false));
		dispatch(setGalleryVpcCode());
		dispatch(clearAdditionalCoverItems());
	};

	const onStartFromScratchClick = (fromCoverOnly = false): void => {
		if (element && resizeObserver) {
			resizeObserver.unobserve(element);
			dispatch(resetIframeHeight());
		}
		dispatch(setCurrentLightbox('none'));
		dispatch(setGalleryPredefinedConfigurationId('blank'));
		dispatch(setPersistedConfiguration(null));
		if (coverOnly && !fromCoverOnly) {
			dispatch(setCoverOnly(false));
		}
		resetSwiper();

		const newHash = UrlService.getReplacedIdHash('blank');
		UrlService.setBrowserWindowHash(newHash);

		dispatch(
			hidePopOver({
				type: 'all',
			})
		);
		dispatch(setGalleryVpcCode());
		if (!fromCoverOnly) {
			dispatch(setProductCardTypeClick({ id: null, type: 'startFromScratch' }));
		}
		dispatch(clearAdditionalCoverItems());
	};

	const onCoverOnlyClick = (): void => {
		if (coverOnlyItems.length && !coverOnlyItems.includes(selectedCover)) {
			dispatch(setSelectedCover(coverOnlyItems[0], 'product-intro-gallery'));
			dispatch(setCoverItem(coverOnlyItems[0], true));
		}
		dispatch(setCoverOnly(true));
		dispatch(clearAdditionalCoverItems());
		onStartFromScratchClick(true);
	};

	const onClose = (): void => {
		if (element && resizeObserver) {
			resizeObserver.unobserve(element);
			dispatch(resetIframeHeight());
		}

		dispatch(setCurrentLightbox('none'));
		resetSwiper();

		dispatch(setCloseGallery('product-intro-gallery'));
	};

	const onWrapperClick = (): void => {
		dispatch(setShowOpenDesign(false));
	};

	const onModalOpen = (e: CustomEvent, productId: string): void => {
		setVisibleModal(e.detail.visibleModal);
		setEcoFeeSheetVisibleId(productId);
	};

	const onModalClose = (): void => {
		setVisibleModal(null);
		setEcoFeeSheetVisibleId(null);
	};

	return (
		<ProductIntroGallery
			settings={settings}
			kompisSettings={kompisSettings}
			kompisTranslations={kompisTranslations}
			onSelectItem={onSelectItem}
			onStartFromScratchClick={onStartFromScratchClick}
			onClose={onClose}
			galleryItems={galleryItems}
			showCoverOnlyCard={coverOnlyItems.length > 0}
			platform={platform}
			deviceType={deviceType}
			isInUpptackaPlatform={isInUpptackaPlatform}
			hasVisitScene={hasVisitScene}
			orientation={orientation}
			writeDirection={writeDirection}
			showScreensaver={showScreensaver}
			ecoFeeSheetVisibleId={ecoFeeSheetVisibleId}
			visibleModal={visibleModal}
			predefinedConfigurations={predefinedConfigurations}
			sofaRangeName={rangeSettings.rangeName}
			onModalOpen={onModalOpen}
			onModalClose={onModalClose}
			nrPerRow={getNrPerRow()}
			nrOfEmptyElements={getNrOfEmptyElements()}
			showProductIntroGallery={currentLightbox === 'product-intro-gallery'}
			startFromScratchImageSrc={rangeSettings.thumbnailsSrc + '/start_from_scratch.png'}
			startFromScratchElementIndex={getStartFromScratchElementIndex()}
			galleryShoppingProducts={galleryShoppingProducts}
			isProduction={isProduction}
			onWrapperClick={onWrapperClick}
			useLocalIds={
				rangeSettings.useLocalIds &&
				rangeSettings.useLocalIds.includes(localeService.getCountryCode())
					? true
					: false
			}
			onCoverOnlyClick={onCoverOnlyClick}
		/>
	);
};

export default ProductIntroGalleryContainer;
